import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { KeyRound, Loader2, Mail, Shield, UserCheck, UserX } from 'lucide-react'
import { formatPhone } from '@/components/erp/phone-input'
import { InviteLinkPanel } from './InviteLinkPanel'
import { DevicesPanel } from './DevicesPanel'
import { SetPinDialog } from './SetPinDialog'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { StatusBadge } from '@/components/erp/primitives'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { useCan, useSession } from '@/shared/auth/session'
import { fmtDateTime, fmtDateTimeSec, humanise } from '@/shared/lib/format'
import { changeStatus, getUser, issueInvitation, requestPasswordReset, userRoles } from './api'
import type {
  InvitationIssuedResponse, UserResponse, UserRoleResponse,
} from '@/shared/api/types'

/**
 * User detail canvas: identity, access, roles, and the lifecycle actions.
 *
 * The two actions that matter most are the invitation and the password reset,
 * because without one of them an account created through the API has no
 * password hash and can never sign in.
 */
export function UserDetailDrawer({
  user, onClose, onChanged,
}: {
  user: UserResponse | null
  onClose: () => void
  onChanged: () => void
}) {
  const canUpdate = useCan('user:update')
  const canCreate = useCan('user:create')
  // Its OWN permission, not user:update — whoever holds it can sign in to the mobile app
  // as this person. See V15_322_003.
  const canSetPin = useCan('user:set-pin')
  const me = useSession((s) => s.user)

  const [full, setFull] = useState<UserResponse | null>(user)
  const [roles, setRoles] = useState<UserRoleResponse[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<{ tone: 'good' | 'bad' | 'info'; text: string } | null>(null)
  /** Set when an invitation was issued but the mail provider refused it. */
  const [mailFailedFor, setMailFailedFor] = useState<string | null>(null)
  /**
   * The last invitation issued from this drawer, kept so its link can be revealed and
   * copied. Item 5: with SMS as the primary channel and no SMS provider in dev, an
   * administrator needs a way to hand the link over directly.
   */
  const [invite, setInvite] = useState<InvitationIssuedResponse | null>(null)
  const [pinOpen, setPinOpen] = useState(false)

  useEffect(() => {
    setFull(user)
    setRoles(null)
    setError(null)
    setNote(null)
    if (!user) return
    let cancelled = false
    setLoading(true)
    Promise.all([
      getUser(user.id),
      userRoles(user.id).catch(() => [] as UserRoleResponse[]),
    ])
      .then(([u, r]) => { if (!cancelled) { setFull(u); setRoles(r) } })
      .catch((err) => { if (!cancelled) setError(explain(err)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [user])

  useEffect(() => { setInvite(null); setMailFailedFor(null) }, [user?.id])

  async function run(key: string, fn: () => Promise<unknown>, ok: string) {
    setBusy(key)
    setNote(null)
    setMailFailedFor(null)
    try {
      const result = await fn()
      const fresh = await getUser(full!.id)
      setFull(fresh)
      // An invitation reports per-channel delivery. Saying "re-issued" when nothing left the
      // building is how somebody waits three days for a message that was never sent — so the
      // result is kept and rendered in full by InviteLinkPanel below.
      const issued = result as InvitationIssuedResponse | undefined
      if (issued && typeof issued.smsSent === 'boolean') {
        setInvite(issued)
        if (!issued.smsSent && !issued.emailSent) {
          setMailFailedFor(fresh.id)
          setNote({ tone: 'bad', text: 'The invitation is valid, but nothing was delivered.' })
        } else {
          setNote({ tone: 'good', text: ok })
        }
      } else {
        setNote({ tone: 'good', text: ok })
      }
      onChanged()
    } catch (err) {
      setNote({ tone: 'bad', text: explain(err) })
    } finally {
      setBusy(null)
    }
  }

  const u = full
  const isSelf = Boolean(u && me && u.id === me.id)

  return (
    <Drawer
      open={Boolean(user)}
      onClose={onClose}
      title={u?.name ?? 'User'}
      subtitle={u ? formatPhone(u.phone) : undefined}
      right={u ? <StatusBadge status={u.status} /> : undefined}
      width="lg"
    >
      <ErrorStrip message={error} />
      {loading && !u && (
        <div className="px-4 py-10 text-center text-erp-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
        </div>
      )}

      {u && (
        <>
          {note && (
            <div className="p-4 pb-0">
              <Note tone={note.tone} onDismiss={() => setNote(null)}>
                {note.text}
                {mailFailedFor && (
                  <>
                    {' '}
                    <Link
                      className="underline"
                      to={`/notifications?recipientUserId=${mailFailedFor}&failedOnly=true`}
                    >
                      The notification log says why
                    </Link>
                    . Transactional mail is configured in the backend environment
                    (<code className="font-mono text-erp-xs">AXIS_ZEPTO_MAIL_*</code>), not in
                    Integrations.
                  </>
                )}
              </Note>
            </div>
          )}

          {/* The state that blocks a person from working, stated plainly. */}
          {u.status === 'INVITED' && (
            <div className="p-4 pb-0">
              <Note tone="info">
                This account has no password yet. It cannot sign in until the invitation
                is accepted.
              </Note>
            </div>
          )}
          {u.lockedUntil && new Date(u.lockedUntil) > new Date() && (
            <div className="p-4 pb-0">
              <Note tone="bad">
                Locked after {u.failedLoginCount} failed attempts. Unlocks {fmtDateTime(u.lockedUntil)}.
              </Note>
            </div>
          )}

          {(canUpdate || canCreate) && (
            <div className="p-4 flex flex-wrap gap-2 border-b border-border">
              {canCreate && u.status === 'INVITED' && (
                <button
                  className="erp-btn"
                  disabled={busy !== null}
                  onClick={() => run('invite',
                    () => issueInvitation(u.id),
                    'Invitation re-issued. Any previous link is now void.')}
                >
                  {busy === 'invite' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                  Resend invitation
                </button>
              )}
              {canUpdate && u.status !== 'INVITED' && (
                <button
                  className="erp-btn"
                  disabled={busy !== null}
                  onClick={() => run('reset',
                    () => requestPasswordReset(u.id),
                    'Reset sent. Their existing sessions have been terminated.')}
                >
                  {busy === 'reset' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
                  Send password reset
                </button>
              )}
              {canSetPin && (
                <button
                  className="erp-btn"
                  disabled={busy !== null}
                  onClick={() => setPinOpen(true)}
                >
                  <KeyRound className="h-3.5 w-3.5" />
                  {u.pinSet ? 'Change mobile PIN' : 'Set mobile PIN'}
                </button>
              )}
              {canUpdate && u.status === 'ACTIVE' && !u.bootstrap && !isSelf && (
                <button
                  className="erp-btn"
                  disabled={busy !== null}
                  onClick={() => run('suspend',
                    () => changeStatus(u.id, 'SUSPENDED'),
                    'Account suspended and all sessions revoked.')}
                >
                  {busy === 'suspend' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserX className="h-3.5 w-3.5" />}
                  Suspend
                </button>
              )}
              {canUpdate && u.status === 'SUSPENDED' && (
                <button
                  className="erp-btn"
                  disabled={busy !== null}
                  onClick={() => run('activate',
                    () => changeStatus(u.id, 'ACTIVE'),
                    'Account reactivated.')}
                >
                  {busy === 'activate' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />}
                  Reactivate
                </button>
              )}
            </div>
          )}

          {u && canSetPin && (
            <SetPinDialog
              open={pinOpen}
              onClose={() => setPinOpen(false)}
              onDone={async () => {
                setFull(await getUser(u.id))
                setNote({ tone: 'good', text: 'Mobile PIN updated.' })
                onChanged()
              }}
              mode="admin"
              userId={u.id}
              userName={u.name}
              hasPin={u.pinSet}
            />
          )}

          {invite && (
            <div className="p-4 border-b border-border">
              <InviteLinkPanel result={invite} phone={u.phone} email={u.email} />
            </div>
          )}

          <DrawerSection title="Identity">
            <DrawerField label="Name" value={u.name} />
            <DrawerField label="Phone" value={formatPhone(u.phone)} />
            <DrawerField label="Email" value={u.email || '— none on file —'} />
            <DrawerField label="User ID" value={u.id} mono />
            <DrawerField
              label="Account type"
              value={u.bootstrap
                ? <span className="inline-flex items-center gap-1.5 text-erp-sm">
                    <Shield className="h-3.5 w-3.5 text-chocolate" /> Bootstrap super-user
                  </span>
                : 'Standard'}
            />
          </DrawerSection>

          {/* Above Access on purpose: when a phone is lost this is the reason
              somebody opened this drawer, and it should not be scrolled to. */}
          <DrawerSection title="Devices">
            <DevicesPanel userId={u.id} userName={u.name} />
          </DrawerSection>

          <DrawerSection title="Access">
            <DrawerField label="Status" value={<StatusBadge status={u.status} />} />
            <DrawerField
              label="Mobile PIN"
              value={u.pinSet ? 'Set' : 'Not set'}
            />
            <DrawerField label="Clearance" value={humanise(u.clearance)} />
            <DrawerField
              label="Scope"
              value={u.scopeUnitId
                ? `${humanise(u.scopeLevel)} · ${u.scopeUnitId}`
                : humanise(u.scopeLevel)}
            />
            <DrawerField
              label="Must change password"
              value={u.mustChangePassword ? 'Yes — on next sign-in' : 'No'}
            />
          </DrawerSection>

          <DrawerSection title={`Roles${roles ? ` (${roles.length})` : ''}`}>
            {roles === null ? (
              <p className="px-4 py-3 text-erp-sm text-muted-foreground">Loading…</p>
            ) : roles.length === 0 ? (
              <p className="px-4 py-3 text-erp-sm text-muted-foreground">
                No roles. This user holds no permissions and will see an empty application.
              </p>
            ) : (
              <div className="px-4 py-3 flex flex-wrap gap-1.5">
                {roles.map((r) => (
                  <span
                    key={r.roleId}
                    title={r.expiresAt ? `Expires ${fmtDateTime(r.expiresAt)}` : 'No expiry'}
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-erp-xs font-medium"
                    style={{
                      borderColor: (r.colour ?? '') || 'var(--color-border)',
                      color: (r.colour ?? '') || undefined,
                    }}
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ background: r.colour ?? 'currentColor' }}
                      aria-hidden
                    />
                    {r.displayName}
                    {!r.active && <span className="opacity-60">(expired)</span>}
                  </span>
                ))}
              </div>
            )}
          </DrawerSection>

          <DrawerSection title="Activity">
            <DrawerField label="Last sign-in" value={fmtDateTimeSec(u.lastLoginAt)} />
            <DrawerField label="Password changed" value={fmtDateTimeSec(u.passwordChangedAt)} />
            <DrawerField
              label="Failed attempts"
              value={<span className="erp-metric">{u.failedLoginCount}</span>}
            />
            <DrawerField label="Locked until" value={fmtDateTimeSec(u.lockedUntil)} />
            <DrawerField label="Created" value={fmtDateTimeSec(u.createdAt)} />
            <DrawerField label="Updated" value={fmtDateTimeSec(u.updatedAt)} />
          </DrawerSection>
        </>
      )}
    </Drawer>
  )
}
